import React, { useState, useEffect } from "react";
import "./RegisterForm.css";
import toast, { Toaster } from "react-hot-toast";
import Confetti from "react-confetti";

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    appointment: "",
    jewelleryName: "",
    name: "",
    email: "",
    whatsapp: "",
    alternate: "",
    location: "",
    product: "",
    priority: "",
    attendedBy: "",
    remarks: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Track window size for confetti
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fieldNames = {
    name: "Name",
    jewelleryName: "Jewellery Name",
    whatsapp: "Whatsapp Number",
    product: "Product",
    priority: "Priority",
    appointment: "Date of Appointment",
    attendedBy: "Attended By",
    location: "Location/City",
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const newErrors = {};
    Object.keys(fieldNames).forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${fieldNames[field]} is required`;
      }
    });
    return newErrors;
  };

  // ✅ WhatsApp API integration
  const sendWhatsAppMessage = async (to, customerName) => {
    try {
      const response = await fetch(
        "https://waapi.atts.in/v1/message/send-message?token=c8274e643737c7b3111bc48a6299fc6b645d72b2cf301198a244ee8fa0152249df2ff3ea4b574e47c7b19efddd46e10f29006fb21db02e7f6e01395900d4a911",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            to: `91${to}`,
            type: "template",
            template: {
              language: {
                policy: "deterministic",
                code: "en",
              },
              name: "expo_cbe",
              components: [
                {
                  type: "header",
                  parameters: [
                    {
                      type: "document",
                      document: {
                        link: "https://aupay-img.s3.eu-north-1.amazonaws.com/aupay/Nyttro_Brochure.pdf",
                        filename: "Nyttro Brochure",
                      },
                    },
                  ],
                },
              ],
            },
          }),
        }
      );

      const result = await response.json();

      if (
        result?.messages &&
        result.messages[0]?.message_status === "accepted"
      ) {
        toast.success("✅ WhatsApp message sent successfully!");
      } else {
        toast.error("⚠️ Failed to send WhatsApp message");
        console.log("WhatsApp API failed response:", result);
      }
    } catch (error) {
      console.error("WhatsApp API error:", error);
      toast.error("❌ WhatsApp API failed.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setIsLoading(false);
      toast.error("⚠️ Please fill all required fields.");
      return;
    }

    try {
      const response = await fetch(
        "https://script.google.com/macros/s/AKfycbz9RWiT0lFgx7QeebCjQQ4wrkxVuPvVB0TAbCij06qa9M6rPHdFlU_BJ4FhjNtEllga/exec",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(formData).toString(),
        }
      );

      const result = await response.json();
      if (result.result === "success") {
        toast.success("🎉 Congratulations! Form submitted successfully!");
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);

        // ✅ WhatsApp message after successful submission
        await sendWhatsAppMessage(formData.whatsapp, formData.name);

        setFormData({
          appointment: "",
          jewelleryName: "",
          name: "",
          email: "",
          whatsapp: "",
          alternate: "",
          location: "",
          product: "",
          priority: "",
          attendedBy: "",
          remarks: "",
        });
        setErrors({});
      } else {
        toast.error("❌ Submission failed. Please try again.");
      }
    } catch (error) {
      toast.error("⚠️ Something went wrong. Please try again.");
      console.error(error);
    }

    setIsLoading(false);
  };
  return (
    <div className="form-container">
      <Toaster position="top-center" />
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          style={{ position: "fixed", top: 0, left: 0, zIndex: 9999 }}
        />
      )}

      <h2 className="form-title">Register to Nyttro</h2>
      <form className="register-form" onSubmit={handleSubmit}>
        {/* Row 1 */}
        <div className="form-row">
          <div className="form-group">
            <label>
              Date of Appointment<span>*</span>
            </label>
            <input
              type="date"
              name="appointment"
              value={formData.appointment}
              onChange={handleChange}
            />
            {errors.appointment && (
              <p className="error">{errors.appointment}</p>
            )}
          </div>

          <div className="form-group">
            <label>
              Jewellery Name<span>*</span>
            </label>
            <input
              name="jewelleryName"
              value={formData.jewelleryName}
              placeholder="Enter jewellery name"
              onChange={handleChange}
            />
            {errors.jewelleryName && (
              <p className="error">{errors.jewelleryName}</p>
            )}
          </div>
        </div>

        {/* Row 2 */}
        <div className="form-row">
          <div className="form-group">
            <label>
              Name<span>*</span>
            </label>
            <input
              name="name"
              value={formData.name}
              placeholder="Enter name"
              onChange={handleChange}
            />
            {errors.name && <p className="error">{errors.name}</p>}
          </div>
          <div className="form-group">
            <label>
              Whatsapp Number<span>*</span>
            </label>
            <input
              name="whatsapp"
              value={formData.whatsapp}
              placeholder="Enter whatsapp number"
              onChange={handleChange}
            />
            {errors.whatsapp && <p className="error">{errors.whatsapp}</p>}
          </div>
        </div>

        {/* Row 3 */}
        <div className="form-row">
          <div className="form-group">
            <label>Alternate Number</label>
            <input
              name="alternate"
              value={formData.alternate}
              placeholder="Enter alternate number"
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>
              Location/City<span>*</span>
            </label>
            <input
              name="location"
              value={formData.location}
              placeholder="Enter location or city"
              onChange={handleChange}
            />
            {errors.location && <p className="error">{errors.location}</p>}
          </div>
        </div>

        {/* Row 4 */}
        <div className="form-row">
          <div className="form-group">
            <label>
              Product<span>*</span>
            </label>
            <select
              name="product"
              value={formData.product}
              onChange={handleChange}
            >
              <option value="">Select a product</option>
              <option value="N-Pay">N-Pay</option>
              <option value="N-ERP">NERP</option>
              <option value="N-Shop">N-Shop</option>
              <option value="N-Loan">N-Loan</option>
              <option value="N-Pay & N-ERP">N-Pay & N-ERP</option>
              <option value="N-Pay & N-Shop">N-Pay & N-Shop</option>
              <option value="Digital Marketing">Digital Marketing</option>
              <option value="Others">Others</option>
            </select>
            {errors.product && <p className="error">{errors.product}</p>}
          </div>

          <div className="form-group">
            <label>
              Priority<span>*</span>
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
            >
              <option value="">Select a priority</option>
              <option value="Hot">Hot</option>
              <option value="Warm">Warm</option>
              <option value="Cold">Cold</option>
            </select>
            {errors.priority && <p className="error">{errors.priority}</p>}
          </div>
        </div>

        {/* Row 5 */}
        <div className="form-row">
          <div className="form-group">
            <label>
              Attended By<span>*</span>
            </label>
            <select
              name="attendedBy"
              value={formData.attendedBy}
              onChange={handleChange}
            >
              <option value="">Select attendee</option>
              <option value="Pothiraj">Pothiraj</option>
              <option value="Kalyanraj">Kalyanraj</option>
              <option value="Vinitha">Vinitha</option>
            </select>
            {errors.attendedBy && <p className="error">{errors.attendedBy}</p>}
          </div>
                    <div className="form-group">
            <label>Email</label>
            <input
              name="email"
              value={formData.email}
              placeholder="Enter email"
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Remarks */}
        <div className="form-group-full">
          <label>Remarks</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            placeholder="Enter remarks"
            onChange={handleChange}
          />
        </div>

        {/* Submit */}
        <button type="submit" disabled={isLoading} className="submit-btn">
          {isLoading ? <span className="loader"></span> : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default RegisterForm;
